import { useEffect, useState } from "react";
import axios from '../../api/axios';
import Sidebar from "../sidebar";
import Navbar from "../navbar";
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, MapPin, Phone, Mail, Check, X } from 'lucide-react';

const RESERVATIONS_URL = '/api/reservation';

const getMoroccoDate = () => {
    return new Date().toISOString().slice(0, 10);
};

function ReservationList() {
    const navigate = useNavigate();
    const [reservations, setReservations] = useState([]);
    const [error, setError] = useState(null);
    const [toggleCount, setToggleCount] = useState(0);
    const [selectedDate, setSelectedDate] = useState(getMoroccoDate);

    const handleReservationClick = (id) => {
        navigate(`/reservations/${id}`);
    };

    const fetchReservations = async (date) => {
        try {
            setError(null);
            const response = await axios.get(`${RESERVATIONS_URL}?date=${date}`);

            const uniqueReservations = Array.from(
                new Map(
                    response.data
                        .sort((a, b) => a.time - b.time)
                        .map(reservation => [reservation.name, reservation])
                ).values()
            );

            setReservations(uniqueReservations);
        } catch (error) {
            setError('Failed to fetch reservations');
            console.error(error);
        }
    };

    const formatTime = (time) => {
        const hours = Math.floor(time);
        const minutes = (time % 1) * 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes === 0 ? '00' : '30'}`;
        return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    useEffect(() => {
        fetchReservations(selectedDate);
        const interval = setInterval(() => fetchReservations(selectedDate), 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [selectedDate]);

    useEffect(() => {
        if (toggleCount > 0) {
            fetchReservations(selectedDate);
        }
    }, [toggleCount, selectedDate]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const toggleArrived = async (id) => {
        try {
            await axios.put(`${RESERVATIONS_URL}/${id}`, { arrived: "arrived" });
            setToggleCount(prev => prev + 1);
        } catch (error) {
            console.log(error);
        }
    };

    const toggleNoshow = async (id) => {
        try {
            await axios.put(`${RESERVATIONS_URL}/${id}`, { arrived: "noshow" });
            setToggleCount(prev => prev + 1);
        } catch (error) {
            console.log(error);
        }
    };

    const getMoroccoDateTime = () => {
        const now = new Date();

        return {
            year: String(now.getUTCFullYear()),
            month: String(now.getUTCMonth() + 1).padStart(2, "0"),
            day: String(now.getUTCDate()).padStart(2, "0"),
            hour: String(now.getUTCHours()).padStart(2, "0"),
            minute: String(now.getUTCMinutes()).padStart(2, "0"),
        };
    };

    const isPastReservation = (reservation) => {
        if (
            reservation.arrived === "cancelled" ||
            reservation.arrived === "arrived" ||
            reservation.arrived === "noshow"
        ) {
            return true;
        }
        const now = getMoroccoDateTime();
        const today = `${now.year}-${now.month}-${now.day}`;

        if (selectedDate < today) return true;
        if (selectedDate > today) return false;

        const currentTime =
            Number(now.hour) + Number(now.minute) / 60;

        return currentTime >= Number(reservation.time) + 0.5;
    };

    const futureReservations = reservations.filter(reservation => !isPastReservation(reservation));
    const pastReservations = reservations.filter(reservation => isPastReservation(reservation));

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="fixed lg:relative lg:flex">
                <Sidebar />
            </div>
            <div className="flex-1">
                <Navbar />
                <div className="flex-1 ml-0 lg:ml-64 p-4">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">
                                Reservations
                            </h2>
                            <div className="flex items-center gap-3 bg-white p-2 rounded-lg shadow-sm">
                                <Calendar className="text-gray-400" size={18} />
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    className="px-3 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <p className="text-gray-600 text-sm font-medium">
                                    {new Date(selectedDate).toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
                            {futureReservations.length > 0 && (
                                <div className="p-2 bg-gradient-to-r from-[#610c63] to-[#9b2c6f] border-b border-[#d3a7d9]">
                                    <h3 className="text-white text-sm font-semibold tracking-wide shadow-md">
                                        Upcoming Reservations
                                    </h3>
                                </div>
                            )}

                            {futureReservations.map((reservation) => (
                                <div
                                    key={reservation._id}
                                    className="p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleReservationClick(reservation._id)}
                                >
                                    <div className="flex flex-col lg:flex-row gap-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h4 className="text-base font-semibold text-gray-900">{reservation.name}</h4>
                                                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                                                    <Users size={12} />
                                                    {reservation.people}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                                <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                    <Clock size={14} />
                                                    {formatTime(reservation.time)}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                    <MapPin size={14} />
                                                    <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                                                        {reservation.zone === 'rooftop' ? 'Rooftop' : 'Salon'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                    <Mail size={14} />
                                                    {reservation.email}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-4">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleArrived(reservation._id);
                                                }}
                                                className="group px-4 py-2 bg-[#610c63] text-white hover:bg-[#7c1f72] hover:scale-105 rounded-md transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                                            >
                                                <Check size={14} className="transition-transform group-hover:scale-110" />
                                                Arrived
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleNoshow(reservation._id);
                                                }}
                                                className="group px-4 py-2 bg-[#9b2c6f] text-white hover:bg-[#610c63] hover:scale-105 rounded-md transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
                                            >
                                                <X size={14} className="transition-transform group-hover:scale-110" />
                                                No Show
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {pastReservations.length > 0 && (
                                <div className="p-2 bg-gray-50 border-b border-gray-100">
                                    <h3 className="text-gray-600 text-sm font-semibold">Past Reservations</h3>
                                </div>
                            )}

                            {pastReservations.map((reservation) => (
                                <div
                                    key={reservation._id}
                                    className="p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer opacity-75"
                                    onClick={() => handleReservationClick(reservation._id)}
                                >
                                    <div className="flex flex-col lg:flex-row gap-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center">
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${reservation.arrived === "arrived" ? "bg-green-100 text-green-700" :
                                                            reservation.arrived === "noshow" ? "bg-red-100 text-red-700" :
                                                                reservation.arrived === "cancelled" ? "bg-gray-200 text-gray-500 line-through" :
                                                                    "bg-gray-100 text-gray-600"
                                                            }`}>
                                                            {reservation.arrived === "arrived" ? "Arrived" :
                                                                reservation.arrived === "noshow" ? "No Show" :
                                                                    reservation.arrived === "cancelled" ? "Cancelled" :
                                                                        "Pending"}
                                                        </span>
                                                    </div>
                                                    <h4 className="text-base font-semibold text-gray-700">{reservation.name}</h4>
                                                </div>
                                                <span className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                                                    <Users size={12} />
                                                    {reservation.people}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                                    <Clock size={14} />
                                                    {formatTime(reservation.time)}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                                    <MapPin size={14} />
                                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                                        {reservation.zone === 'rooftop' ? 'Rooftop' : 'Salon'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                                    <Mail size={14} />
                                                    {reservation.email}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${reservation.arrived === "arrived" ? "bg-green-100 text-green-700" :
                                                reservation.arrived === "noshow" ? "bg-red-100 text-red-700" :
                                                    "bg-gray-100 text-gray-600"
                                                }`}>
                                                {reservation.arrived === "arrived" ? "Arrived" :
                                                    reservation.arrived === "noshow" ? "No Show" :
                                                        "Pending"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReservationList;